<template>
    <div class="card">
        <div class="bg-blue-light p-1">
            <TitleButton
                :showBtn="true"
                :showAddNew="false"
                title="Template Sales Invoice"
                @onClickCloseButton="goToList"
            >
                <template #extra-btn>
                    <div class="template-selection">
                        <label class="form-label">Templete Sales Invoice</label>
                        <AsyncSelect
                            placeholder="Select Template"
                            v-model="template"
                            class="w-100"
                            :additional-query="{ template_type: 'sale'}"
                            :api-service="fetchTemplates"
                        />
                    </div>
                </template>
            </TitleButton> 
        </div>
        <div class="row p-2">
            <div class="col-12 col-sm-6 col-md-4 col-lg-4">
                <div class="d-flex align-items-center justify-content-between filter-label">
                    <label for="colFormLabel" class="col-form-label">Customer Name: </label>
                </div>
                <AsyncSelect placeholder="Select Customer Name" 
                    v-model="formData.contact_profile_id"
                    :api-service="fetchContactProfiles"
                    :reduce="text => text.id"
                />
            </div>
            <div class="col-12 col-sm-6 col-md-4 col-lg-4">
                <div class="d-flex align-items-center justify-content-between filter-label">
                    <label for="colFormLabel" class="col-form-label">PO Reference </label>
                </div>
                <vField
                    name="reference"
                    type="text"
                    class="form-control"
                    placeholder="PO Reference"
                    v-model="formData.order_number"
                />
            </div>
        </div>

        <div class="row px-2 pb-2">
            <div class="col-12 col-sm-6 col-md-4 col-lg-4">
                <div class="d-flex align-items-center justify-content-between filter-label">
                    <label for="colFormLabel" class="col-form-label">Ship To: </label>
                </div>
                <vField
                    as="textarea"
                    name="destination_address"
                    type="text"
                    class="form-control"
                    placeholder="Ship To"
                    v-model="formData.destination_address"
                />
            </div>
            <div class="col-12 col-sm-6 col-md-4 col-lg-4">
                <div class="d-flex align-items-center justify-content-between filter-label">
                    <label for="colFormLabel" class="col-form-label">Ship From (Warehouse): </label>
                </div>
                <v-select
                    placeholder="Select Warehouse Location"
                    v-model="formData.location_id"
                    :options="locations"
                    label="text"
                    :reduce="text => text.id"
                />
            </div>
        </div>

        <div class="col-12 px-2 pb-2">
            <div class="col-12 col-sm-6 col-md-4 col-lg-4">
                <div class="d-flex align-items-center justify-content-between filter-label">
                    <label for="colFormLabel" class="col-form-label">Transport Type & Number </label>
                </div>
                <vField
                    name="transport_reference"
                    type="text"
                    class="form-control"
                    placeholder="Transport Type & Number"
                    v-model="formData.transport_reference"
                />
            </div>
        </div>

        <div class="row px-2 pb-2">
            <div class="col-md-12 col-lg-4">
                <AsyncSelect
                    placeholder="Select Product"
                    v-model="productId"
                    :api-service="fetchProductList"
                    :reduce="name => name.id"
                    :format-label="option => option.text"
                    :additional-query="{ location_id: formData.location_id }"
                    label="text"
                />
            </div>
            <div class="col-md-4 col-lg-2">
                <vField
                    name="quantity"
                    type="number"
                    class="form-control"
                    placeholder="Quantity"
                    v-model="quantity"
                />
            </div>
            <div class="col-md-4 col-lg-2">
                <button class="btn btn-primary" @click="addNewItem">
                    Add
                </button>
            </div>

          <div class="col-md-12 col-lg-4">
              <div class="mb-1">
                <v-select
                    placeholder="Select Vat Type"
                    v-model="formData.vat_type"
                    :options="vatTypeList"
                    label="name"
                    :reduce="name => name.value"
                />
              </div>
            </div>
        </div>

        <div class="col-12 px-2">
            <ListTable
                :tableItems="tableItems"
                :discount-account-heads="discountAccountHeads"
                @addNewItem="addNewItem"
                @handleSubmit="handleSubmit"
                :submitLoading="submitLoading"
                :isHospitalInvoice="false"
                :type="'template_invoice'"
            />
        </div>
        <GlobalLoader />
        <Loader v-if="loader"/>
    </div>
</template>

<script setup>
import TitleButton from '@/components/atom/TitleButton';
import ListTable from '@/components/molecule/company/hospital/investigation/CreateIpdBillingTable.vue';
import { onMounted, ref, inject, provide, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import GlobalLoader from "@/components/atom/GlobalLoader.vue";
import { useStore } from "vuex";
import useDate from "@/services/utils/day";
import handlePurchase from "@/services/modules/purchase";
import handleCompany from "@/services/modules/company";
import handleBusinessesLocations from "@/services/modules/businessesLocations";
import handleTemplate from '@/services/modules/all-modules/template';
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";
import Loader from '@/components/atom/LoaderComponent'
import { computed } from 'vue';
import handleContact from '@/services/modules/contact';
import handleInventory from "@/services/modules/inventory";
import handleBarcode from '@/services/modules/barcode';
import pdfPrinter from '@/services/utils/pdfPrinter';
import handleHospital from '@/services/modules/hospital';
import handleChartAccount from '@/services/modules/chartAccount'

const $route = useRoute();
const $router = useRouter();
const $store = useStore();
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const { fetchSalesBill, fetchProductById, fetchInvoiceTemplateSalesBill, fetchProductList, ...rest } = handlePurchase();
const { fetchCompanyInfo, fetchActiveCustomizationField } = handleCompany();
const {fetchBusinessLocationsList} = handleBusinessesLocations()
const {currentDate} = useDate();
const { fetchTemplates } = handleTemplate();
const { fetchContactProfiles } = handleContact();
const { getProduct } = handleInventory();
const { exportToPDF } = pdfPrinter();
const { fetchBarcode } = handleBarcode();
const { notesByType } = handleHospital();
const { formatDate } = useDate();
const { fetchHome } = handleChartAccount()

const itemSkeleton = {
    name: '',
    description: '',
    quantity: null,
    rate: 0,
    discount_percent: 0,
    vat: 0,
    sub_total: 0,
    discount_amount: 0,
    vat_amount: 0,
    total: 0,
    amount: 0,
    total_amount: 0,
    is_edit: false,
};

const company = ref({});
const locations = ref([]);
const tableItems = ref([]);
const discountAccountHeads = ref([]);
const submitLoading = ref(false);
const invoiceRes = ref(null);
const template = ref(null);
const loader = ref(false);
const formHeader = ref(null);
const productId = ref(null);
const quantity = ref(1);
const companyInfo = ref({});
const invoiceNotes = ref([]);
const barcodeQuery = ref('');
const barcode = ref('');
const qrcode = ref('');
const customizationPdfFields = ref([]);
const vatTypeList = ref([]);
const companyId = $route.params.companyId;
const user = computed(() => $store.state.user.user);

const getInitialForm = () => {
    return {
        company_id: companyId,
        contact_profile_id: null,
        account_head_id: null,
        ipd_register_id: null,
        vat_payable_account_head_id: null,
        project_id: null,
        cost_centre_id: null,
        location_id: null,
        mop_account_head_id: null,
        discount_head_id: null,
        service_resource_id: null,
        receipt_reference: null,
        adjustment_amount: null,
        referrer_id: null,
        receipt_description: "Sales invoice",
        payment: 0,
        status: "active",
        date: currentDate(),
        has_item_detail: true,
        description: "",
        item_details: [],
        sale_type: "general_invoice",
        receipt_type: "general_invoice",
        order_number: '',
        destination_address: '',
        transport_reference: '',
        vat_type: 'vat_exclusive',
    }
}

const formData = ref(getInitialForm());

provide('formData', formData);

const addNewItem = async () => {
    if (!productId.value) {
        showError("Please select a product");
        return;
    }

    const found = !!tableItems.value.find(item => item.product_id === productId.value);
    if (found) {
        showError('Item already selected!');
        return;
    }

    const productResp = await fetchProductById(
        productId.value,
        `?company_id=${companyId}&location_id=${formData.value.location_id}`
    );

    if (!productResp.data) {
        showError("Something went wrong. Please try again.");
        return;
    }

    const selected = productResp.data;

    const newItem = {
        product_id: selected.id,
        name: selected.name + (selected.stock_in_hand ? ` (Stock: ${selected.stock_in_hand})` : ''),
        product_type: selected.product_type,
        description: '',
        quantity: quantity.value || 1,
        rate: selected.sales_price || 0,
        discount: 0,
        vat: 0,
        sub_total: 0,
        discount_amount: 0,
        discount_percent: 0,
        vat_amount: 0,
        total: 0,
        is_edit: false,
        product: {
            name: selected.name
        }
    };

    tableItems.value.push(newItem);

    productId.value = null;
    quantity.value = 1;
};

const goToList = () => {
    $router.push({
        name: 'sales-invoice-template-list',
        params: {
            companyId: $route.params.companyId,
            moduleId: $route.params.moduleId,
            menuId: $route.params.menuId,
            pageId: $route.params.pageId
        },
        query: $route.query
    })
}

const handleSubmit = async () => {
    if (!formData.value.date) formData.value.date = currentDate();
    formData.value.item_details = getFormattedTableItems()

    let copyFormData = Object.assign({}, formData.value);
    copyFormData.item_details = formData.value.item_details
    copyFormData.sale_type = "general_invoice"
    copyFormData.receipt_type = "general_invoice"
    copyFormData.validate_stock = true;

    submitLoading.value = true;

    await rest.storeBillPayment(copyFormData)
        .then(res => {
            if (res.status) {
                showSuccess(res.message)
                goToPrint(res.data.id, 1)
                goToList();
                return;
            }
            showError(res.message)
        })
        .catch(err => {
            showError(err.response.data.message)
        })

    submitLoading.value = false
}

const getFormattedTableItems = () => {
    return tableItems.value
        .filter(item => item.quantity !== 0)
        .map((item) => {
        item.amount = item.sub_total;
        item.total_amount = item.total;
        return item
    })
}

const resetForm = () => {
    tableItems.value = [];
    template.value = null;

    const newFormData = getInitialForm();
    newFormData.account_head_id = formData.value.account_head_id;
    newFormData.vat_payable_account_head_id = formData.value.vat_payable_account_head_id;
    newFormData.discount_head_id = formData.value.discount_head_id;
    newFormData.location_id = formData.value.location_id;

    formData.value = newFormData;
    if(formHeader.value) formHeader.value.resetData();
}

onMounted(async () => {
  try {
    const res = await fetchCompanyInfo($route.params.companyId);
    companyInfo.value = res.data;
  } catch (error) {
    console.log(error);
  }

  await getInvoiceNotes();
  await getActiveFiledCustomization();
});

const getInvoiceNotes = async () => {
  const companyQuery = `?company_id=${$route.params.companyId}&show_default_only=1`;
  const res = await notesByType(companyQuery, 'invoice_terms');
  if (res.status) {
    invoiceNotes.value = res.data.filter((item) => item.full_desc);
  }
};

const getActiveFiledCustomization = async () => {
  const companyQuery = `?company_id=${$route.params.companyId}&customization_type=pdf&type=sale_invoice_pdf_list`;
  const res = await fetchActiveCustomizationField(companyQuery);
  customizationPdfFields.value = res.data;
};

const goToPrint = async (id, btnFunc) => {
  try {
    const companyQuery = `?company_id=${$route.params.companyId}`;
    const res = await fetchSalesBill(id, companyQuery);

    if (res) {
      invoiceRes.value = res.data;
      const qrcodeData = JSON.stringify({
        ref_no: invoiceRes.value.bill_number,
        ref_date: invoiceRes.value.date,
        created_by: invoiceRes.value.created_by_name,
        created_date_time: formatDate(new Date())
      });

      barcodeQuery.value = `?code=${invoiceRes.value.bill_number}&qrcode=${qrcodeData}`;

      const barcodeRes = await fetchBarcode(barcodeQuery.value);
      barcode.value = barcodeRes.barcode;
      qrcode.value = barcodeRes.qrcode;

      exportToPDF(
          companyInfo.value,
          invoiceRes.value,
          barcode.value,
          qrcode.value,
          user.value.name,
          btnFunc,
          invoiceNotes.value,
          false,
          customizationPdfFields.value
      );
    }
  } catch (err) {
    console.log(err);
  }
};

const setTableItems = (data) => {
    
    if(!data.general || data.general.length < 1) return;

    let vatType = data.vat_type
    if (vatType === null || vatType === ''){
      vatType = 'vat_exclusive'
    }
    formData.value.vat_type = vatType

    tableItems.value = data.general.map((item) => {
        return {
            ...itemSkeleton,
            product_id: item.product_id,
            product_type: item.product_type,
            name: item.product_name + '(Stock: ' + (item.stock_in_hand || 0) + ')',
            quantity: 0,
            rate: item.rate || 0,
            description: item.description || '',
            discount_percent: item.discount_percent || 0,
            discount_amount: item.discount_amount || 0,
            vat: item.vat || 0,
            vat_amount: item.vat_amount || 0,
            batch_number: item.batch_number
        }
    })
}

const getSingleSalesBill = async () => {

    const id = template.value && template.value.templateable_id;
    if(!id) return;

    let companyQuery = `?company_id=${companyId}`;
    if(formData.value.location_id) companyQuery += `&location_id=${formData.value.location_id}`;

    loader.value = true;

    await fetchInvoiceTemplateSalesBill(id, companyQuery).then( (res) =>{
        if(! res.data) return;
        setTableItems(res.data);
    }).catch( (err) => {
        console.log(err);
    }).finally(() => {
        loader.value = false;
    })
}
watch(() => formData.value.location_id, (newValue) => {
  if (newValue) {
    getSingleSalesBill();
  }
});

watch(template, (newValue) => {
    if(newValue) {
        getSingleSalesBill();
    }
});

onMounted(() => {
    const companyQuery = `?company_id=${companyId}`;
    const locationQuery = `${companyQuery}&location_type=fg_warehouse`;

    $store.commit('setLoading', true);

    Promise.all([
        rest.fetchAccountReceivable(companyQuery).then(res => {
            if (res.data) formData.value.account_head_id = res.data[0].id;
        }),
        rest.fetchVatPayable(companyQuery).then(res => {
            if (res.data) formData.value.vat_payable_account_head_id = res.data[0].id;
        }),
        fetchCompanyInfo(companyId).then(res => {
            if (res.data) company.value = res.data;
        }),
        rest.getAccountHeadBySlag("discount_account", companyQuery).then(res => {
            if (res.data) {
                discountAccountHeads.value = res.data;
                formData.value.discount_head_id = res.data.length ? res.data[0].id : null;
            }
        }),
        fetchBusinessLocationsList(locationQuery).then(res=> {
            if(res.data) {
                locations.value = res.data
                formData.value.location_id = res.data[0].id
            }
        }),
        fetchHome().then(res => {
          if (!res.data) {
            return;
          }
          if (res.data && res.data.vat_type){
            vatTypeList.value = res.data.vat_type;
          }
        })
    ])
    .finally(() => $store.commit('setLoading', false))
})
</script>

<style scoped>
address {
    margin-bottom: 0 !important;
}
.template-selection {
    width: 400px;
}
</style>